import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useStore } from '../hooks/useStore';
import { getTournament, getTournamentBySlug } from '../services/tournamentService';
import type { Division, Game, Team, Tournament } from '../models';

/**
 * Active-tournament context.
 *
 * Anywhere in the app a page renders inside `/t/:tournamentId/...` (admin
 * workspace) or `/e/:slug/...` (public event view), this provider resolves
 * the tournament from the URL and exposes it + memoized children. Pages stop
 * independently picking "the first one." When real auth lands, the same
 * context surface still applies.
 */

interface TournamentContextValue {
  tournament: Tournament;
  divisions: Division[];
  teams: Team[];
  games: Game[];
}

const TournamentContext = createContext<TournamentContextValue | null>(null);

interface ProviderProps {
  /** The resolved tournament (parent should already have validated it exists). */
  tournament: Tournament;
  children: ReactNode;
}

export function TournamentProvider({ tournament, children }: ProviderProps) {
  // Subscribe to the whole db (stable reference — only changes when updateDb is called).
  // Filter inside useMemo so we don't pass a new array reference from the selector on every
  // render, which would cause useSyncExternalStore to detect a constant snapshot change and
  // loop indefinitely (React Error #185).
  const db = useStore((d) => d);
  const divisions = useMemo(
    () => db.divisions.filter((d) => d.tournamentId === tournament.id),
    [db.divisions, tournament.id],
  );
  const teams = useMemo(
    () => db.teams.filter((t) => t.tournamentId === tournament.id),
    [db.teams, tournament.id],
  );
  const games = useMemo(
    () => db.games.filter((g) => g.tournamentId === tournament.id),
    [db.games, tournament.id],
  );

  const value = useMemo<TournamentContextValue>(
    () => ({ tournament, divisions, teams, games }),
    [tournament, divisions, teams, games],
  );

  return <TournamentContext.Provider value={value}>{children}</TournamentContext.Provider>;
}

/**
 * Resolves `:tournamentId` from the route. If the id is missing or invalid,
 * redirects home. Render this inline at the workspace root.
 */
export function TournamentByIdProvider({ children }: { children: ReactNode }) {
  const params = useParams();
  const id = params.tournamentId ?? params.id;
  // Support both internal IDs (tour-xxx) and URL-friendly slugs.
  const tournament = useStore(() => {
    if (!id) return undefined;
    return getTournament(id) ?? getTournamentBySlug(id);
  });
  if (!tournament) {
    return (
      <div className="card" style={{ margin: 24 }}>
        <h3 className="card__title">Event not found</h3>
        <p className="card__sub" style={{ marginTop: 4 }}>
          This event is no longer in your local database — it may have been cleared on page refresh.
        </p>
        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <Link to="/admin/seasons" className="btn btn--primary btn--sm">Back to seasons</Link>
          <Link to="/admin" className="btn btn--ghost btn--sm">Dashboard</Link>
        </div>
      </div>
    );
  }
  return <TournamentProvider tournament={tournament}>{children}</TournamentProvider>;
}

/**
 * Resolves `:slug` from the route (public event URLs).
 */
export function TournamentBySlugProvider({ children }: { children: ReactNode }) {
  const { slug } = useParams();
  const tournament = useStore(() => (slug ? getTournamentBySlug(slug) : undefined));
  if (!tournament) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Event not found</div>
        <Link to="/events">Back to events</Link>
      </div>
    );
  }
  return <TournamentProvider tournament={tournament}>{children}</TournamentProvider>;
}

export function useActiveTournament(): TournamentContextValue {
  const ctx = useContext(TournamentContext);
  if (!ctx) {
    throw new Error(
      'useActiveTournament must be used inside <TournamentProvider> (i.e. under /t/:tournamentId or /e/:slug)',
    );
  }
  return ctx;
}

/**
 * Soft variant: returns null when no tournament is in scope. Useful for shared
 * pages that may render in either workspace or stand-alone contexts.
 */
export function useActiveTournamentMaybe(): TournamentContextValue | null {
  return useContext(TournamentContext);
}
